// Form controls shared by the sign-in pages (staff and patient portal), so both keep one look.
import { useState } from 'react';
import { EyeIcon, EyeSlashIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme.jsx';

export const LOGIN_ACCENT = '#1C8A7D';
const LOGIN_ACCENT_HOVER = '#157F73';

const INPUT_CLASS = 'relative block w-full max-w-full box-border rounded-lg border-0 py-3 px-4 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:z-10 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-colors';

/** Props for a sign-in text input: the shared look plus the accent focus ring. */
export function loginInputProps(extraClass = '') {
  return {
    className: extraClass ? `${INPUT_CLASS} ${extraClass}` : INPUT_CLASS,
    style: { '--tw-ring-color': LOGIN_ACCENT },
    onFocus: (e) => { e.target.style.outline = `2px solid ${LOGIN_ACCENT}`; },
    onBlur: (e) => { e.target.style.outline = 'none'; },
  };
}

/** Light/dark switch, saved under the same preference the app uses so the choice carries past sign-in. */
export function LoginThemeToggle() {
  const { isDark, setTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center !rounded-lg !border !border-solid !border-gray-300 !bg-transparent !p-0 !shadow-none text-gray-600 hover:!bg-gray-100 dark:!border-gray-700 dark:text-gray-300 dark:hover:!bg-gray-800"
    >
      {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
    </button>
  );
}

/** Password input with a show/hide button. */
export function LoginPasswordInput({ id = 'password', value, onChange, autoComplete = 'current-password', placeholder = 'Enter your password' }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        name={id}
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={onChange}
        {...loginInputProps('pr-11')}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShown((current) => !current)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        className="absolute right-2 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center !rounded-md !border-0 !bg-transparent !p-0 !shadow-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        {shown ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
      </button>
    </div>
  );
}

/** The red notice under the fields when sign-in fails. */
export function LoginErrorNotice({ children }) {
  return (
    <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{children}</p>
        </div>
      </div>
    </div>
  );
}

/** Full-width accent submit button with a spinner while busy. */
export function LoginSubmitButton({ busy, disabled, busyLabel, children }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="group relative flex justify-center rounded-lg py-3 px-8 text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full"
      style={{ backgroundColor: LOGIN_ACCENT, borderColor: LOGIN_ACCENT }}
      onMouseEnter={(e) => { e.target.style.backgroundColor = LOGIN_ACCENT_HOVER; }}
      onMouseLeave={(e) => { e.target.style.backgroundColor = LOGIN_ACCENT; }}
    >
      {busy ? (
        <div className="flex items-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {busyLabel}
        </div>
      ) : children}
    </button>
  );
}
